import type { ComponentType } from 'react';
import { CloseCircle, LinkExternal02 } from '@kula-ai/icons-core';
import { fetchSelectOptions } from '@/components/formBuilder/helpers/formHelpers';
import { Box } from '@chakra-ui/react';
import * as Yup from 'yup';
import { FormBuilder } from '@/components/formBuilder';
import { KText } from '@/ui-library/components/text';

const archiveFormFieldConfig = [
  {
    name: 'archiveInput',
    type: 'input',
    isRequired: true,
    placeholder: 'Type "ARCHIVE"',
    errorMessage: 'Confirmation message is required',
    validation: Yup.string()
      .oneOf(['ARCHIVE'], `Confirmation message doesn't match`)
      .required('Confirmation message is required'),
  },
];

const INACTIVE_JOB_STATES = ['archived', 'closed'];
const ARCHIVABLE_JOB_STATES = ['draft', 'closed'];

/**
 * `jobActions` replaces the `dispatch` this used to take in the same slot: the
 * clone and archive thunks are now the `useCloneJob` / `useArchiveJob`
 * mutations, so the callers no longer need a Redux dispatch at all. Both reject
 * on failure rather than resolving with an `errors` array.
 */
export const jobsTableRowConfig = (
  history,
  jobDetails,
  toast,
  jobActions,
  type = 'table',
  formRef,
  isCreateAllowed = false,
  _feJobListingChanges?: boolean,
  // Handed in by the page, never imported here: the modal's own imports lead
  // back to this file, and importing it was 7 new circular dependencies.
  // Null hides the action, so callers pass it only when
  // t_close_job_bulk_reject is on.
  CloseJobModalComponent: ComponentType<{
    jobId: string | number;
    modalActions: { isOpen: boolean; onClose: () => void };
  }> | null = null
) => {
  // No fallbacks: this slot used to be `dispatch`, and a caller still passing
  // that would otherwise get a no-op that toasts success for an archive that
  // never happened. Better to throw here than to fake the outcome.
  const { cloneJob, archiveJob } = jobActions;
  // The card is built per job; the table is built once for the grid and gets
  // its job per row at call time.
  const resolveJob = (row) => (type === 'card' ? jobDetails : row) || {};
  const isActive = (row) =>
    !INACTIVE_JOB_STATES.includes(resolveJob(row)?.state);

  const config = [
    {
      id: 'edit',
      label: 'Edit job',
      enabled: (row) => Boolean(resolveJob(row)?.can_edit),
      isConditional: true,
      checkCondition: isActive,
      onClick: (onClose, currentClickedRow) => {
        const job = resolveJob(currentClickedRow);
        const tab = job?.state === 'draft' ? 'jobInformation' : 'settings';
        history.push(`/jobs/${job?.id}?tab=${tab}`);
        onClose();
      },
    },
    {
      id: 'preview',
      label: 'Preview job',
      icon: LinkExternal02,
      iconAlignRight: true,
      enabled: (row) => {
        const job = resolveJob(row);
        return !(job?.job_post_kind === 'internal' || job?.state === 'draft');
      },
      isConditional: true,
      checkCondition: isActive,
      onClick: async (onClose, currentClickedRow) => {
        try {
          const jobId = resolveJob(currentClickedRow)?.id;
          const apiConfig = {
            processResponse: (res) => res,
            endPoint: `ats_jobs/${jobId}/ats_job_post/preview`,
          };
          const response = await fetchSelectOptions(apiConfig);
          const { errors } = response || {};
          const { preview_url } = response?.data || {};
          if (preview_url) {
            window.open(preview_url, '_blank');
            onClose();
          }
          if (errors?.length > 0) {
            toast({
              title: `Please save draft to get preview`,
              description: '',
              status: 'error',
            });
          }
        } finally {
          // setPreviewLoading(false);
        }
      },
    },
    {
      id: 'clone',
      label: 'Clone job',
      enabled: isCreateAllowed,
      modalConfig: {
        title: 'Clone job',
        // description: 'Please confirm to clone this job',
        successButtonText: 'Clone',
        cancelButtonText: 'Cancel',
        isActionLoading: false,
        handleOnClose: (onClose) => {
          onClose();
        },
        isModalActionLoading: false,
        isButtonDisabled: false,
        onModalSubmit: async (currentClickedRow, onClose, loadingAction) => {
          const jobId = resolveJob(currentClickedRow)?.id;

          loadingAction(true);
          try {
            const response = await cloneJob({ jobId });
            // A 200 whose body carries no job is not a clone to land on.
            if (response?.id) {
              toast({ title: 'Job cloned successfully' });
              onClose();
              history.push(`/jobs/${response.id}?isClonedJob=true`);
            } else {
              toast({ title: 'Error in cloning job', status: 'error' });
            }
          } catch (error) {
            toast({
              title: 'Error in cloning job',
              description: error instanceof Error ? error.message : undefined,
              status: 'error',
            });
          } finally {
            loadingAction(false);
          }
        },
        RenderContent: ({ selected }) => {
          const jobName = resolveJob(selected)?.name;
          return (
            <KText fontSize="sm" color="secondary-700">
              Cloning this job
              <KText as="span" size="sm-semi-bold" color="secondary-700">
                {' '}
                {jobName}{' '}
              </KText>
              will create a duplicate without any candidates
            </KText>
          );
        },
      },
    },
    {
      id: 'archive',
      label: 'Archive job',
      enabled: (row) => Boolean(resolveJob(row)?.can_edit),
      isConditional: true,
      checkCondition: (row) =>
        ARCHIVABLE_JOB_STATES.includes(resolveJob(row)?.state),
      modalConfig: {
        title: 'Are you sure?',
        description: `Archiving a job is permanent and cannot be undone`,
        successButtonText: 'Archive',
        cancelButtonText: 'Cancel',
        isActionLoading: false,
        handleOnClose: (onClose) => {
          onClose();
        },
        isModalActionLoading: false,
        isButtonDisabled: false,
        onModalSubmit: (selected, onClose, loadingAction) => {
          if (formRef?.current) {
            const currentValues = formRef.current.values;
            const newValues = {
              ...currentValues,
              selected,
              onClose,
              loadingAction,
            };
            formRef?.current?.setValues(newValues);
            formRef?.current?.submitForm();
          }
        },
        RenderContent: () => (
          <Box>
            <FormBuilder
              config={archiveFormFieldConfig}
              onSubmit={async (values) => {
                const {
                  selected = {},
                  onClose = () => {},
                  loadingAction = () => {},
                } = values;
                loadingAction(true);
                try {
                  // The mutation invalidates the list on success; a refetch
                  // here on top of that was a second request for the same key.
                  await archiveJob({ jobId: resolveJob(selected)?.id });
                  toast({ title: 'Job archived successfully' });
                  onClose();
                } catch (error) {
                  // The thunk unwrapped to `data` and then checked
                  // `!response?.errors?.length` on it — undefined on a failure,
                  // so the check passed and this toasted success for a job that
                  // was never archived.
                  toast({
                    title: 'Error in archiving a job',
                    description:
                      error instanceof Error ? error.message : undefined,
                    status: 'error',
                  });
                } finally {
                  loadingAction(false);
                }
              }}
              formRef={formRef}
            />
          </Box>
        ),
      },
    },
    {
      id: 'close',
      label: 'Close job',
      icon: CloseCircle,
      iconProps: { color: 'error-200', boxSize: '16px' },
      color: 'error-200',
      enabled: (row) => Boolean(resolveJob(row)?.can_edit),
      isConditional: true,
      checkCondition: (row) =>
        Boolean(CloseJobModalComponent) &&
        !INACTIVE_JOB_STATES.includes(resolveJob(row)?.state),
      modalConfig: {
        isCustomModal: true,
        RenderContent: ({ selected, modalActions }) => (
          <CloseJobModalComponent
            jobId={resolveJob(selected)?.id}
            modalActions={modalActions}
          />
        ),
      },
    },
  ];
  return config;
};

export { jobsNewFilterConfig } from './jobs-filter-config';
